import { useCallback, useEffect, useState } from 'react';

import { CancelStep } from '../cancelModalSteps';

interface UseWizardNavigationParams {
  visibleSteps: CancelStep[];
  initialStep: CancelStep;
  // Reset to initialStep when this flips false (modal closing).
  opened: boolean;
  // Optional gate called before advancing — return false to block.
  beforeAdvance?: (from: CancelStep) => boolean;
}

interface WizardNavigation {
  currentStep: CancelStep;
  goNext: () => void;
  goBack: () => void;
  isFirstStep: boolean;
  isLastStep: boolean;
}

export default function useWizardNavigation({
  visibleSteps,
  initialStep,
  opened,
  beforeAdvance,
}: UseWizardNavigationParams): WizardNavigation {
  const [currentStep, setCurrentStep] = useState<CancelStep>(initialStep);

  // Reset wizard position when the modal closes.
  useEffect(() => {
    if (!opened) {
      setCurrentStep(initialStep);
    }
  }, [opened, initialStep]);

  // If the active step is no longer in the visible list (e.g., user changed inputs so a
  // step is now skipped), snap to the last visible step instead of leaving the wizard
  // pointed at a hidden step.
  useEffect(() => {
    if (!visibleSteps.includes(currentStep)) {
      setCurrentStep(visibleSteps[visibleSteps.length - 1]);
    }
  }, [visibleSteps, currentStep]);

  const currentIndex = Math.max(0, visibleSteps.indexOf(currentStep));
  const isFirstStep = currentIndex === 0;
  const isLastStep = currentIndex === visibleSteps.length - 1;

  const goNext = useCallback(() => {
    if (beforeAdvance && !beforeAdvance(currentStep)) {
      return;
    }
    const next = visibleSteps[currentIndex + 1];
    if (next) {
      setCurrentStep(next);
    }
  }, [beforeAdvance, currentStep, currentIndex, visibleSteps]);

  const goBack = useCallback(() => {
    const prev = visibleSteps[currentIndex - 1];
    if (prev) {
      setCurrentStep(prev);
    }
  }, [currentIndex, visibleSteps]);

  return { currentStep, goNext, goBack, isFirstStep, isLastStep };
}
